// Session management utility
import { accessControl } from './accessControl';

const SESSION_TIMEOUT = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

export const sessionManager = {
  // Store login data with timestamp
  storeLoginData: (userData) => {
    const loginData = {
      userData: userData,
      timestamp: Date.now(),
      expiresIn: SESSION_TIMEOUT
    };
    localStorage.setItem('usibras_login', JSON.stringify(loginData));
    // Also store the userData directly for backward compatibility
    localStorage.setItem('usibras_user_data', userData);
  },

  // Check if session is still valid
  isSessionValid: () => {
    const loginData = localStorage.getItem('usibras_login');
    if (!loginData) return false;
    
    try {
      const parsedData = JSON.parse(loginData);
      const currentTime = Date.now();
      const sessionAge = currentTime - parsedData.timestamp;
      
      return sessionAge <= parsedData.expiresIn;
    } catch (error) {
      console.error('Error parsing login data:', error);
      return false;
    }
  },

  // Get user data if session is valid
  getUserData: () => {
    if (!sessionManager.isSessionValid()) {
      sessionManager.clearSession();
      return null;
    }
    
    try {
      const loginData = localStorage.getItem('usibras_login');
      const parsedData = JSON.parse(loginData);
      return parsedData.userData;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  },

  // Clear session data
  clearSession: () => {
    localStorage.removeItem('usibras_login');
    // Clear access control permissions
    accessControl.clearPermissions();
  },

  // Get remaining session time in minutes
  getRemainingTime: () => {
    const loginData = localStorage.getItem('usibras_login');
    if (!loginData) return 0;
    
    try {
      const parsedData = JSON.parse(loginData);
      const currentTime = Date.now();
      const sessionAge = currentTime - parsedData.timestamp;
      const remainingTime = parsedData.expiresIn - sessionAge;
      
      return Math.max(0, Math.floor(remainingTime / (60 * 1000))); // Return minutes
    } catch (error) {
      return 0;
    }
  },

  // Set up automatic session check (call this in your main App component)
  setupSessionCheck: (onSessionExpired) => {
    // Check every minute
    const interval = setInterval(() => {
      if (!sessionManager.isSessionValid()) {
        sessionManager.clearSession();
        if (onSessionExpired) {
          onSessionExpired();
        }
        clearInterval(interval);
      }
    }, 60 * 1000); // Check every minute

    // Also check when user becomes active (tab focus, mouse movement, etc.)
    const handleUserActivity = () => {
      if (!sessionManager.isSessionValid()) {
        sessionManager.clearSession();
        if (onSessionExpired) {
          onSessionExpired();
        }
        clearInterval(interval);
        // Remove event listeners
        document.removeEventListener('mousemove', handleUserActivity);
        document.removeEventListener('keypress', handleUserActivity);
        document.removeEventListener('click', handleUserActivity);
        document.removeEventListener('focus', handleUserActivity);
      }
    };

    // Add event listeners for user activity
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('keypress', handleUserActivity);
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('focus', handleUserActivity);

    return () => {
      clearInterval(interval);
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('keypress', handleUserActivity);
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('focus', handleUserActivity);
    };
  }
}; 